import { assumptionDefinitionMarkdown } from "@hightouch/lib/llm/agentic-settings-entities/definition-markdown";
import type { InlineCitationRefs } from "@hightouch/lib/llm/inline-citation-tags";
import type { ResponseMessage } from "@hightouch/lib/llm/schema";
import { AgentDefinitionStatus } from "src/graphql";
import { isJourneyGraphEnvelopeContent } from "src/pages/journeys/utils/journey-output-adapter";

export type InteractionResponseMessage = ResponseMessage & {
  interactionId?: string;
};

type ResponseMessageOfType<T extends ResponseMessage["type"]> = Extract<
  ResponseMessage,
  { type: T }
>;

type AssumptionsMessageContent =
  ResponseMessageOfType<"assumptions">["content"];
type AssumptionEntry = AssumptionsMessageContent["assumptions"][number];
type ReferencedMemoryEntry =
  AssumptionsMessageContent["referencedMemories"][number];

/** An entry of an assumptions message, tagged with the array it came from. */
export type CitedDefinitionEntry =
  | { kind: "assumption"; entry: AssumptionEntry; interactionId: string }
  | {
      kind: "referenced";
      entry: ReferencedMemoryEntry;
    };

export type InteractionCitations = {
  /** Its new assumptions followed by the saved definitions it referenced, including entries no tag can cite. */
  entries: CitedDefinitionEntry[];
  /** corresponds to the 'assumptions` array in the message's content */
  byAssumptionKey: ReadonlyMap<string, CitedDefinitionEntry>;
  /** corresponds to the `referencedMemories` array in the message's content */
  byDefinitionId: ReadonlyMap<string, CitedDefinitionEntry>;
};

export type SessionOutputItem = {
  key: string;
  message: ResponseMessageOfType<"csv" | "pptx">;
};

export type SessionResourceType = "audience" | "journey" | "trait";

type SessionResourceMessageType = SessionResourceType | "journey_reference";

/** The resource the host page shows, so its Data row doesn't reopen it in a new tab. */
export type SessionResource = { type: SessionResourceType; id: string };

export type SessionResourceItem = {
  key: string;
  resourceType: SessionResourceType;
  /** The latest message carrying the resource. */
  message: ResponseMessageOfType<SessionResourceMessageType>;
  /** Null for a draft. */
  resourceId: string | null;
  name: string;
  status: "created" | "referenced" | "draft";
  messageIndex: number;
};

export type SessionContext = {
  citationsByInteractionId: Map<string, InteractionCitations>;
  /** Every cited entry across the session, deduped, most recent first. */
  citedEntries: CitedDefinitionEntry[];
  outputs: SessionOutputItem[];
  resources: SessionResourceItem[];
};

type DeriveSessionContextArgs = {
  messages: readonly InteractionResponseMessage[];
  activeInteractionId: string | null;
};

export function deriveSessionContext(
  args: DeriveSessionContextArgs,
): SessionContext {
  const completedInteractionIds = getCompletedInteractionIds(args);
  // Each value array holds its interaction's messages oldest first; the keys are inserted newest interaction first.
  const assumptionsMessagesByInteractionId = new Map<
    string,
    ResponseMessageOfType<"assumptions">[]
  >();
  const outputRowsByKey = new Map<string, SessionOutputItem>();
  const resourceRowsByKey = new Map<string, SessionResourceItem>();

  // Walks newest first, so the first sighting of an item places it.
  for (
    let messageIndex = args.messages.length - 1;
    messageIndex >= 0;
    messageIndex--
  ) {
    const message = args.messages[messageIndex];
    if (
      !message?.interactionId ||
      !completedInteractionIds.has(message.interactionId)
    ) {
      continue;
    }

    switch (message.type) {
      case "assumptions": {
        const interactionMessages = assumptionsMessagesByInteractionId.get(
          message.interactionId,
        );
        if (interactionMessages) {
          interactionMessages.unshift(message);
        } else {
          assumptionsMessagesByInteractionId.set(message.interactionId, [
            message,
          ]);
        }
        break;
      }
      case "csv":
      case "pptx": {
        const outputRow = buildOutputItem(message, message.interactionId);
        if (!outputRowsByKey.has(outputRow.key)) {
          outputRowsByKey.set(outputRow.key, outputRow);
        }
        break;
      }
      case "audience":
      case "journey":
      case "journey_reference":
      case "trait": {
        const resourceRow = buildResourceItem(message, messageIndex);
        const newerResourceRow = resourceRowsByKey.get(resourceRow.key);
        if (!newerResourceRow) {
          resourceRowsByKey.set(resourceRow.key, resourceRow);
        } else if (resourceRow.status === "created") {
          resourceRowsByKey.set(resourceRow.key, {
            ...newerResourceRow,
            status: "created",
          });
        }
        break;
      }
    }
  }

  const citationsByInteractionId = new Map(
    [...assumptionsMessagesByInteractionId].map(
      ([interactionId, assumptionsMessages]) => [
        interactionId,
        buildInteractionCitations(interactionId, assumptionsMessages),
      ],
    ),
  );
  const allCitedEntries = [...citationsByInteractionId.values()].flatMap(
    ({ entries }) => entries,
  );
  return {
    citationsByInteractionId,
    citedEntries: dedupeCitedEntries(allCitedEntries),
    outputs: [...outputRowsByKey.values()],
    resources: [...resourceRowsByKey.values()],
  };
}

export function getLastAssistantMessageIndexesByInteractionId(
  messages: readonly InteractionResponseMessage[],
): Map<string, number> {
  const indexes = new Map<string, number>();
  messages.forEach((message, index) => {
    if (message.type === "assistant" && message.interactionId) {
      indexes.set(message.interactionId, index);
    }
  });
  return indexes;
}

function getCompletedInteractionIds(
  args: DeriveSessionContextArgs,
): Set<string> {
  const completedInteractionIds = new Set<string>();
  const endedInteractionIds = new Set<string>();
  for (const message of args.messages) {
    if (!message.interactionId) continue;
    completedInteractionIds.add(message.interactionId);
    // An interaction with an error or canceled message is dropped entirely.
    // Its resource/file cards are presented in the final message, which is never persisted.
    if (message.type === "error" || message.type === "canceled") {
      endedInteractionIds.add(message.interactionId);
    }
  }
  for (const endedInteractionId of endedInteractionIds) {
    completedInteractionIds.delete(endedInteractionId);
  }
  if (args.activeInteractionId) {
    completedInteractionIds.delete(args.activeInteractionId);
  }
  return completedInteractionIds;
}

export function citedEntryTitle(citedEntry: CitedDefinitionEntry): string {
  return citedEntry.kind === "assumption"
    ? citedEntry.entry.assumption
    : citedEntry.entry.title;
}

export function citedEntryMarkdown(citedEntry: CitedDefinitionEntry): string {
  return citedEntry.kind === "assumption"
    ? assumptionDefinitionMarkdown(citedEntry.entry)
    : citedEntry.entry.content;
}

export type CitedVersion = {
  versionId: string;
  status: AgentDefinitionStatus;
};

// Null for a turn processed before versions were stamped onto messages, or a reference whose id wasn't served that turn.
export function citedEntryVersion(
  citedEntry: CitedDefinitionEntry,
): CitedVersion | null {
  const { versionId } = citedEntry.entry;
  if (!versionId) return null;
  if (citedEntry.kind === "assumption") {
    return { versionId, status: AgentDefinitionStatus.Assumed };
  }
  const { status } = citedEntry.entry;
  return status ? { versionId, status } : null;
}

// Null when the entry was never saved as a definition, or the agent omitted the id.
export function citedEntryDefinitionId(
  citedEntry: CitedDefinitionEntry,
): string | null {
  return citedEntry.entry.definitionId ?? null;
}

// Definition titles aren't unique, so an id-less entry is identified by its title and body together.
function citedEntryContentKey(citedEntry: CitedDefinitionEntry): string {
  return `content:${JSON.stringify([citedEntryTitle(citedEntry), citedEntry.entry.content])}`;
}

export function citedEntryKey(citedEntry: CitedDefinitionEntry): string {
  return citedEntryDefinitionId(citedEntry) ?? citedEntryContentKey(citedEntry);
}

// Validation rejects a repeated key or id within one assumptions message, so the first entry wins only across messages.
function buildInteractionCitations(
  interactionId: string,
  assumptionsMessages: ResponseMessageOfType<"assumptions">[],
): InteractionCitations {
  const assumptionEntries: CitedDefinitionEntry[] = [];
  const referencedEntries: CitedDefinitionEntry[] = [];
  const byAssumptionKey = new Map<string, CitedDefinitionEntry>();
  const byDefinitionId = new Map<string, CitedDefinitionEntry>();
  for (const { content } of assumptionsMessages) {
    for (const entry of content.assumptions) {
      const citedEntry: CitedDefinitionEntry = {
        kind: "assumption",
        entry,
        interactionId,
      };
      assumptionEntries.push(citedEntry);
      if (entry.key && !byAssumptionKey.has(entry.key)) {
        byAssumptionKey.set(entry.key, citedEntry);
      }
    }
    for (const entry of content.referencedMemories ?? []) {
      const citedEntry: CitedDefinitionEntry = { kind: "referenced", entry };
      referencedEntries.push(citedEntry);
      if (entry.definitionId && !byDefinitionId.has(entry.definitionId)) {
        byDefinitionId.set(entry.definitionId, citedEntry);
      }
    }
  }
  return {
    entries: dedupeCitedEntries([...assumptionEntries, ...referencedEntries]),
    byAssumptionKey,
    byDefinitionId,
  };
}

/** The entries one inline tag cites, in tag order, deduped, with references its interaction doesn't resolve dropped. */
export function resolveParagraphCitedEntries(
  citations: InteractionCitations | undefined,
  { assumptionKeys, referencedIds }: InlineCitationRefs,
): CitedDefinitionEntry[] {
  if (!citations) return [];
  return dedupeCitedEntries([
    ...assumptionKeys.flatMap(
      (key) => citations.byAssumptionKey.get(key) ?? [],
    ),
    ...referencedIds.flatMap((id) => citations.byDefinitionId.get(id) ?? []),
  ]);
}

// Entries with an id dedupe by it. An id-less entry folds into the entry with
// the same title and body that has an id, or else dedupes by title and body.
function dedupeCitedEntries(
  citedEntries: CitedDefinitionEntry[],
): CitedDefinitionEntry[] {
  const definitionIdsByContentKey = new Map<string, string>();
  for (const citedEntry of citedEntries) {
    const definitionId = citedEntryDefinitionId(citedEntry);
    const contentKey = citedEntryContentKey(citedEntry);
    if (definitionId && !definitionIdsByContentKey.has(contentKey)) {
      definitionIdsByContentKey.set(contentKey, definitionId);
    }
  }

  const citedEntriesByKey = new Map<string, CitedDefinitionEntry>();
  for (const citedEntry of citedEntries) {
    const definitionId = citedEntryDefinitionId(citedEntry);
    const contentKey = citedEntryContentKey(citedEntry);
    const entryKey =
      definitionId ?? definitionIdsByContentKey.get(contentKey) ?? contentKey;
    const placedEntry = citedEntriesByKey.get(entryKey);
    if (
      !placedEntry ||
      (!citedEntryDefinitionId(placedEntry) && definitionId)
    ) {
      citedEntriesByKey.set(entryKey, citedEntry);
    }
  }
  return [...citedEntriesByKey.values()];
}

function buildOutputItem(
  message: ResponseMessageOfType<"csv" | "pptx">,
  interactionId: string,
): SessionOutputItem {
  const { content } = message;
  return {
    key: `${message.type}:${content.fileReference ?? `${interactionId}:${content.filename}`}`,
    message,
  };
}

function buildResourceItem(
  message: ResponseMessageOfType<SessionResourceMessageType>,
  messageIndex: number,
): SessionResourceItem {
  const { createdResourceId, referencedResourceId, name } =
    extractResourceIdsAndName(message);
  const resourceId = createdResourceId ?? referencedResourceId;
  const resourceType =
    message.type === "journey_reference" ? "journey" : message.type;
  return {
    // Drafts key by name, so an agent revising one unsaved resource only results in a single entry.
    key: resourceId
      ? `${resourceType}:${resourceId}`
      : `${resourceType}:draft:${name}`,
    resourceType,
    message,
    resourceId,
    name,
    status: createdResourceId
      ? "created"
      : referencedResourceId
        ? "referenced"
        : "draft",
    messageIndex,
  };
}

function extractResourceIdsAndName(
  message: ResponseMessageOfType<SessionResourceMessageType>,
): {
  createdResourceId: string | null;
  referencedResourceId: string | null;
  name: string;
} {
  switch (message.type) {
    case "audience":
    case "trait":
      return {
        createdResourceId: normalizeResourceId(message.content.newId),
        referencedResourceId: normalizeResourceId(message.content.existingId),
        name: message.content.name,
      };
    case "journey": {
      const { content } = message;
      if (isJourneyGraphEnvelopeContent(content)) {
        return {
          createdResourceId: normalizeResourceId(content.newId),
          referencedResourceId: normalizeResourceId(content.journey.id),
          name: content.journey.name,
        };
      }
      return {
        createdResourceId: normalizeResourceId(content.newId),
        referencedResourceId: null,
        name: content.name,
      };
    }
    case "journey_reference":
      return {
        createdResourceId: null,
        referencedResourceId: normalizeResourceId(message.content.existingId),
        name: message.content.name,
      };
  }
}

function normalizeResourceId(
  resourceId: string | number | null | undefined,
): string | null {
  return resourceId == null || resourceId === "" ? null : String(resourceId);
}
